import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import { green } from "@mui/material/colors";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import IconButton from "@mui/material/IconButton";
import RemoveIcon from "@mui/icons-material/Remove";
import { Button, CircularProgress, InputBase } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Paper from "@mui/material/Paper";

interface IShoppingCartItemProps {
  rowParams: any;
  updateCartLoading: boolean;
  handleAvailableCart: (packageAvailable: number) => void;
  /** When true, shows a labeled "Add to cart" button instead of the cart icon. */
  useAddToCartButton?: boolean;
}

function clampQty(n: number, max: number): number {
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, max);
}

export const BuyAction: React.FunctionComponent<IShoppingCartItemProps> = ({
  rowParams,
  updateCartLoading,
  handleAvailableCart,
  useAddToCartButton = false,
}) => {
  const max = rowParams?.row?.available ?? 1;
  const rowKey = rowParams?.id ?? rowParams?.row?.id;
  const [packageAvailable, setPackageAvailable] = useState<number>(1);

  useEffect(() => {
    setPackageAvailable(1);
  }, [rowKey, max]);

  const handleChangePackageAvailable = (isPlus: boolean = true) => {
    setPackageAvailable((prev) => {
      const next = isPlus ? prev + 1 : prev - 1;
      return clampQty(next, max);
    });
  };

  const commitQtyFromInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "");
    if (digits === "") {
      setPackageAvailable(1);
      return;
    }
    setPackageAvailable(clampQty(parseInt(digits, 10), max));
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mt: useAddToCartButton ? 0 : 2,
        marginTop: 0,
        flexWrap: useAddToCartButton ? "wrap" : "nowrap",
        gap: useAddToCartButton ? 1 : 0,
      }}
    >
      <Paper sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 100, height: 32 }}>
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <IconButton
            size="small"
            color="primary"
            sx={{ p: 0 }}
            onClick={() => handleChangePackageAvailable(false)}
            disabled={packageAvailable <= 1 || updateCartLoading}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <InputBase
            sx={{ flex: 1, minWidth: 0, input: { textAlign: "center", padding: "2px 0" } }}
            value={packageAvailable}
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              "aria-label": "Quantity to add",
            }}
            onChange={(e) => commitQtyFromInput(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
          <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />
          <IconButton
            size="small"
            color="primary"
            sx={{ p: 0 }}
            onClick={() => handleChangePackageAvailable()}
            disabled={packageAvailable >= max || updateCartLoading}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "end", alignItems: "center", pr: useAddToCartButton ? 0 : 2, gap: 1 }}>
        {rowParams.value != null && rowParams.value !== "" ? <span>{rowParams.value}</span> : null}
        {useAddToCartButton ? (
          <Button
            variant="contained"
            color="success"
            size="small"
            disabled={updateCartLoading || max < 1}
            onClick={(e) => {
              e.stopPropagation();
              handleAvailableCart(packageAvailable);
            }}
            endIcon={updateCartLoading ? <CircularProgress color="inherit" size={16} /> : undefined}
          >
            Add to cart
          </Button>
        ) : (
          <ShoppingCartOutlinedIcon
            sx={{ ml: 2, mr: 2, cursor: "pointer", color: green[500] }}
            onClick={(e) => {
              e.stopPropagation();
              handleAvailableCart(packageAvailable);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default BuyAction;

